/** Scenes 3–4: «Город — день / ночь» from the driver's seat (docs/SCENES.md §3–4). */

import {
  ellipseDeg, fillRectDeg, geom, hash01, polygonDeg, rectDeg, roundRectPath, setFont, textDeg,
} from './draw-utils';
import { t, type Lang } from '../i18n';
import { NIGHT_TONE, RENDER_H, RENDER_W, type Scene, type SceneLight } from './types';

const HFOV = 40;
const G = geom(HFOV);
const HORIZON = 1.5;
const VP: readonly [number, number] = [0.5, 1.5];

interface Palette {
  night: boolean;
  skyTop: string;
  skyHorizon: string;
  ground: string;
  road: string;
  laneLine: string;
  building: string[];
  windowLit: string;
  windowDark: string;
  tree: string;
  trunk: string;
  carBody: string;
  carGlass: string;
  oncomingBody: string;
  headlight: string;
  taillight: string;
  signBlue: string;
  signText: string;
  plateBg: string;
  plateText: string;
  pole: string;
  lampHead: string;
  neonGreen: string;
  neonBlue: string;
  neonWarm: string;
  signBoard: string;
}

const DAY: Palette = {
  night: false,
  skyTop: '#5fa9e6',
  skyHorizon: '#d7ebf8',
  ground: '#b9b3a7',
  road: '#6a6b70',
  laneLine: '#f4f4f0',
  building: ['#d9d3c9', '#c8c1b5', '#e2dcd2', '#bfb7ab'],
  windowLit: '#9db6c9',
  windowDark: '#9db6c9',
  tree: '#4f8f3a',
  trunk: '#6b4a2b',
  carBody: '#3a5fa0',
  carGlass: '#2a3441',
  oncomingBody: '#d8d8d8',
  headlight: '#f2f2e6',
  taillight: '#c0392b',
  signBlue: '#1d4ea3',
  signText: '#ffffff',
  plateBg: '#1d4ea3',
  plateText: '#ffffff',
  pole: '#6c6f74',
  lampHead: '#d9dde2',
  neonGreen: '#1e9e4a',
  neonBlue: '#2456b8',
  neonWarm: '#c96a1e',
  signBoard: '#ffffff',
};

/** Night: diffuse surfaces around 0.02–0.05 linear (sRGB ≈ 0.15–0.25). */
const NIGHT: Palette = {
  night: true,
  skyTop: '#0c1120',
  skyHorizon: '#232a3a',
  ground: '#2a2a2c',
  road: '#2b2b2f',
  laneLine: '#6c6c66',
  building: ['#2e2c2a', '#2a2826', '#312f2c', '#282624'],
  windowLit: '#e6bf6c',
  windowDark: '#1b1b1f',
  tree: '#1e2c1c',
  trunk: '#26201a',
  carBody: '#1c2534',
  carGlass: '#141a22',
  oncomingBody: '#23262c',
  headlight: '#fff7e0',
  taillight: '#ff3a2e',
  signBlue: '#13315f',
  signText: '#b9c5d8',
  plateBg: '#13315f',
  plateText: '#b9c5d8',
  pole: '#3a3c40',
  lampHead: '#ffe6b0',
  neonGreen: '#3ddc73',
  neonBlue: '#5f8cf0',
  neonWarm: '#f0a050',
  signBoard: '#3a3a3a',
};

function drawFar(ctx: CanvasRenderingContext2D, p: Palette): void {
  // Sky.
  const sky = ctx.createLinearGradient(0, 0, 0, G.Y(HORIZON));
  sky.addColorStop(0, p.skyTop);
  sky.addColorStop(1, p.skyHorizon);
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, RENDER_W, RENDER_H);
  // Ground (sidewalks) below the horizon.
  fillRectDeg(ctx, G, -20, 20, -12.2, HORIZON, p.ground);

  drawSkyline(ctx, p);
  drawRoad(ctx, p);
  drawTrees(ctx, p);
  drawShops(ctx, p);
  drawLamps(ctx, p);
  drawTrafficLight(ctx, p);
  drawOverheadSign(ctx, p);
  drawSpeedSign(ctx, p);
  drawStreetPlate(ctx, p);
  drawOncoming(ctx, p, -2.6, 0.3, 2.4, 1.4, 1.0);
  drawOncoming(ctx, p, -2.2, 0.9, 1.5, 0.9, 0.65);
  drawCarAhead(ctx, p);
}

function drawSkyline(ctx: CanvasRenderingContext2D, p: Palette): void {
  // Buildings recede toward the vanishing point; heights shrink toward the centre.
  const blocks: [x0: number, x1: number, h: number][] = [
    [-20, -16.5, 9.5], [-16.5, -13, 7.5], [-13, -10.5, 8.5], [-10.5, -8.2, 6], [-8.2, -6.4, 5],
    [-6.4, -5, 4.2], [-5, -3.9, 3.4], [-3.9, -3, 2.6], [-3, -2.2, 2],
    [2.4, 3.2, 2.2], [3.2, 4.1, 2.9], [4.1, 5.3, 3.6], [5.3, 6.8, 4.5], [6.8, 8.6, 5.4],
    [8.6, 11, 6.5], [11, 14, 7.8], [14, 17.2, 6.8], [17.2, 20, 9],
  ];
  blocks.forEach(([x0, x1, h], i) => {
    const colour = p.building[i % p.building.length]!;
    fillRectDeg(ctx, G, x0, x1, HORIZON - 0.2, HORIZON + h, colour);
    const cols = Math.max(1, Math.floor((x1 - x0) / 0.55));
    const rows = Math.max(1, Math.floor(h / 0.75));
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const wx0 = x0 + 0.12 + c * ((x1 - x0 - 0.2) / cols);
        const wy0 = HORIZON + 0.35 + r * (h / rows);
        const lit = !p.night || hash01(i * 97 + r * 13 + c) > 0.55;
        const rr = rectDeg(G, wx0, wx0 + 0.3, wy0, wy0 + 0.4);
        ctx.fillStyle = lit ? p.windowLit : p.windowDark;
        ctx.fillRect(rr.x, rr.y, rr.w, rr.h);
      }
    }
  });
}

function drawRoad(ctx: CanvasRenderingContext2D, p: Palette): void {
  polygonDeg(ctx, G, [[-20, -12.2], [20, -12.2], [VP[0] + 0.6, VP[1]], [VP[0] - 0.6, VP[1]]]);
  ctx.fillStyle = p.road;
  ctx.fill();
  // Curbs.
  ctx.strokeStyle = p.night ? '#3a3a3c' : '#d8d3c8';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(G.X(-20), G.Y(-12.2));
  ctx.lineTo(G.X(VP[0] - 0.6), G.Y(VP[1]));
  ctx.moveTo(G.X(20), G.Y(-12.2));
  ctx.lineTo(G.X(VP[0] + 0.6), G.Y(VP[1]));
  ctx.stroke();
  // Centre line (dashed) left of our lane, and the right edge line.
  const dashed = (xBottom: number): void => {
    for (let k = 0; k < 14; k++) {
      const t0 = 0.06 + k * 0.068;
      const t1 = t0 + 0.034;
      const w0 = 0.05 + t0 * 0.35;
      const w1 = 0.05 + t1 * 0.35;
      const x0 = VP[0] + (xBottom - VP[0]) * t0;
      const y0 = VP[1] + (-12.2 - VP[1]) * t0;
      const x1 = VP[0] + (xBottom - VP[0]) * t1;
      const y1 = VP[1] + (-12.2 - VP[1]) * t1;
      polygonDeg(ctx, G, [[x0 - w0, y0], [x0 + w0, y0], [x1 + w1, y1], [x1 - w1, y1]]);
      ctx.fillStyle = p.laneLine;
      ctx.fill();
    }
  };
  dashed(-7);
  ctx.strokeStyle = p.laneLine;
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.moveTo(G.X(15), G.Y(-12.2));
  ctx.lineTo(G.X(VP[0] + 0.45), G.Y(VP[1]));
  ctx.stroke();
}

function drawTrees(ctx: CanvasRenderingContext2D, p: Palette): void {
  const trees: [x: number, base: number, size: number][] = [
    [-17, -2.5, 2.4], [-12.5, -0.8, 1.7], [-9.5, 0.2, 1.2], [-7.8, 0.6, 0.9],
    [17.5, -2.2, 2.2], [13.5, -0.5, 1.6], [10.8, 0.3, 1.1], [9.4, 0.7, 0.8],
  ];
  for (const [x, base, s] of trees) {
    fillRectDeg(ctx, G, x - s * 0.08, x + s * 0.08, base, base + s * 0.9, p.trunk);
    ctx.fillStyle = p.tree;
    ellipseDeg(ctx, G, x, base + s * 1.3, s * 0.55, s * 0.6);
    ctx.fill();
  }
}

function drawShops(ctx: CanvasRenderingContext2D, p: Palette): void {
  // Sign boards; the lettering is drawn by drawNeon (emissive at night).
  fillRectDeg(ctx, G, -15.5, -8.8, 2.55, 3.65, p.signBoard);
  fillRectDeg(ctx, G, -7.6, -4.6, 2.8, 3.55, p.signBoard);
  fillRectDeg(ctx, G, 13.6, 17.4, 5.0, 5.95, p.signBoard);
  if (!p.night) drawNeon(ctx, p);
}

/** Shop lettering: DORIXONA with a green cross, KAFE, BANK. At night this is the emissive layer. */
function drawNeon(ctx: CanvasRenderingContext2D, p: Palette): void {
  ctx.fillStyle = p.neonGreen;
  const cross = rectDeg(G, -15.2, -14.5, 2.75, 3.45);
  ctx.fillRect(cross.x + cross.w * 0.35, cross.y, cross.w * 0.3, cross.h);
  ctx.fillRect(cross.x, cross.y + cross.h * 0.35, cross.w, cross.h * 0.3);
  textDeg(ctx, G, 'DORIXONA', -14.2, 2.85, 0.5, p.neonGreen, 800);
  textDeg(ctx, G, 'KAFE', -6.1, 3.0, 0.42, p.neonWarm, 800, 'center');
  textDeg(ctx, G, 'BANK', 15.5, 5.25, 0.55, p.neonBlue, 800, 'center');
}

const LAMPS: [x: number, base: number, top: number][] = [
  [4.3, 0.9, 7.2], [6.2, 0.5, 6.4], [8.7, -0.1, 5.6], [12.2, -1.0, 4.9], [16.6, -2.1, 4.3], [19.6, -2.9, 4.0],
];

function drawLamps(ctx: CanvasRenderingContext2D, p: Palette): void {
  ctx.strokeStyle = p.pole;
  ctx.lineCap = 'round';
  for (const [x, base, top] of LAMPS) {
    ctx.lineWidth = Math.max(2, (top - base) * 0.06 * G.ppd * 0.25);
    ctx.beginPath();
    ctx.moveTo(G.X(x), G.Y(base));
    ctx.lineTo(G.X(x), G.Y(top));
    ctx.lineTo(G.X(x - (top - base) * 0.12), G.Y(top));
    ctx.stroke();
    ctx.fillStyle = p.lampHead;
    ellipseDeg(ctx, G, x - (top - base) * 0.12, top - 0.05, (top - base) * 0.04, (top - base) * 0.025);
    ctx.fill();
  }
}

function drawTrafficLight(ctx: CanvasRenderingContext2D, p: Palette): void {
  const x = -6;
  fillRectDeg(ctx, G, x - 0.07, x + 0.07, 0.5, 7.6, p.pole);
  const box = rectDeg(G, x - 0.42, x + 0.42, 7.5, 9.5);
  roundRectPath(ctx, box.x, box.y, box.w, box.h, 6);
  ctx.fillStyle = '#1a1c20';
  ctx.fill();
  const lamps: [y: number, colour: string][] = [
    [9.0, p.night ? '#ff2a1e' : '#e8352a'],
    [8.5, p.night ? '#3a2a10' : '#8a6a20'],
    [8.0, p.night ? '#10301a' : '#2f7a3a'],
  ];
  for (const [y, colour] of lamps) {
    ctx.fillStyle = colour;
    ellipseDeg(ctx, G, x, y, 0.25, 0.25);
    ctx.fill();
  }
}

function drawOverheadSign(ctx: CanvasRenderingContext2D, p: Palette): void {
  // Gantry from the right sidewalk.
  fillRectDeg(ctx, G, 7.9, 8.15, 0.6, 8.2, p.pole);
  fillRectDeg(ctx, G, -1.9, 8.15, 7.95, 8.2, p.pole);
  const s = rectDeg(G, -1.9, 1.9, 5.55, 7.45);
  ctx.fillStyle = p.signBlue;
  ctx.fillRect(s.x, s.y, s.w, s.h);
  ctx.strokeStyle = p.signText;
  ctx.lineWidth = 2;
  ctx.strokeRect(s.x + 3, s.y + 3, s.w - 6, s.h - 6);
  textDeg(ctx, G, 'Toshkent ↑', -1.5, 6.75, 0.24 * 1.5, p.signText, 600);
  textDeg(ctx, G, 'Chilonzor →', -1.5, 5.95, 0.24 * 1.5, p.signText, 600);
}

function drawSpeedSign(ctx: CanvasRenderingContext2D, p: Palette): void {
  const x = 9.4;
  const y = 3.2;
  fillRectDeg(ctx, G, x - 0.06, x + 0.06, 0.4, y, p.pole);
  ctx.fillStyle = p.night ? '#b03030' : '#d62b2b';
  ellipseDeg(ctx, G, x, y, 0.8, 0.8);
  ctx.fill();
  ctx.fillStyle = p.night ? '#d0d0d0' : '#ffffff';
  ellipseDeg(ctx, G, x, y, 0.62, 0.62);
  ctx.fill();
  setFont(ctx, 0.55 * G.ppd, 800);
  ctx.fillStyle = '#111';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('60', G.X(x), G.Y(y));
  ctx.textBaseline = 'alphabetic';
}

function drawStreetPlate(ctx: CanvasRenderingContext2D, p: Palette): void {
  const s = rectDeg(G, 10.8, 18.6, 3.45, 4.35);
  ctx.fillStyle = p.plateBg;
  ctx.fillRect(s.x, s.y, s.w, s.h);
  textDeg(ctx, G, 'Amir Temur shoh koʻchasi', 11.1, 3.7, 0.38, p.plateText, 600);
}

function drawOncoming(ctx: CanvasRenderingContext2D, p: Palette, cx: number, cy: number, w: number, h: number, lightDx: number): void {
  const x0 = cx - w / 2;
  const x1 = cx + w / 2;
  const y0 = cy - h * 0.45;
  const y1 = cy + h * 0.55;
  const body = rectDeg(G, x0, x1, y0, y1);
  roundRectPath(ctx, body.x, body.y, body.w, body.h, h * 0.18 * G.ppd);
  ctx.fillStyle = p.oncomingBody;
  ctx.fill();
  fillRectDeg(ctx, G, x0 + w * 0.15, x1 - w * 0.15, cy + h * 0.1, y1 - h * 0.08, p.carGlass);
  ctx.fillStyle = p.headlight;
  ellipseDeg(ctx, G, cx - lightDx, cy - h * 0.2, w * 0.075, h * 0.08);
  ctx.fill();
  ellipseDeg(ctx, G, cx + lightDx, cy - h * 0.2, w * 0.075, h * 0.08);
  ctx.fill();
  fillRectDeg(ctx, G, x0 + w * 0.3, x1 - w * 0.3, cy - h * 0.42, cy - h * 0.3, p.night ? '#0e0e10' : '#333');
}

function drawCarAhead(ctx: CanvasRenderingContext2D, p: Palette): void {
  const x0 = -1.0;
  const x1 = 2.4;
  const y0 = -0.8;
  const y1 = 1.9;
  // Wheels / shadow.
  fillRectDeg(ctx, G, x0 + 0.05, x1 - 0.05, y0 - 0.1, y0 + 0.25, '#151618');
  const body = rectDeg(G, x0, x1, y0 + 0.1, y1);
  roundRectPath(ctx, body.x, body.y, body.w, body.h, 0.35 * G.ppd);
  ctx.fillStyle = p.carBody;
  ctx.fill();
  // Rear window.
  fillRectDeg(ctx, G, x0 + 0.45, x1 - 0.45, 0.95, y1 - 0.25, p.carGlass);
  // Taillights.
  ctx.fillStyle = p.taillight;
  ellipseDeg(ctx, G, -0.55, 0.6, 0.22, 0.14);
  ctx.fill();
  ellipseDeg(ctx, G, 1.95, 0.6, 0.22, 0.14);
  ctx.fill();
  // Plate.
  fillRectDeg(ctx, G, 0.15, 1.25, -0.2, 0.12, p.night ? '#9a9a90' : '#ffffff');
  setFont(ctx, 0.17 * G.ppd, 700);
  ctx.fillStyle = '#111';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('01 A 777 AA', G.X(0.7), G.Y(-0.04));
  ctx.textBaseline = 'alphabetic';
}

function drawMid(ctx: CanvasRenderingContext2D, p: Palette): void {
  // Dashboard.
  const dash = rectDeg(G, -20, 20, -12.2, -7.5);
  const grad = ctx.createLinearGradient(0, dash.y, 0, dash.y + dash.h);
  grad.addColorStop(0, p.night ? '#202226' : '#33353a');
  grad.addColorStop(1, p.night ? '#121316' : '#1f2124');
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.moveTo(0, RENDER_H);
  ctx.lineTo(0, dash.y + 10);
  ctx.quadraticCurveTo(RENDER_W / 2, dash.y - 14, RENDER_W, dash.y + 10);
  ctx.lineTo(RENDER_W, RENDER_H);
  ctx.closePath();
  ctx.fill();

  drawCluster(ctx, p);
  drawNav(ctx, p);

  // Windshield frame: A-pillars, roof band, mirror.
  const frame = p.night ? '#0d0e10' : '#1c1d20';
  polygonDeg(ctx, G, [[-20, -7.5], [-18.4, -7.5], [-16.6, 12.2], [-20, 12.2]]);
  ctx.fillStyle = frame;
  ctx.fill();
  polygonDeg(ctx, G, [[20, -7.5], [18.4, -7.5], [16.6, 12.2], [20, 12.2]]);
  ctx.fill();
  fillRectDeg(ctx, G, -20, 20, 11, 12.2, frame);
  fillRectDeg(ctx, G, -0.15, 0.15, 10.4, 11.2, frame);
  const m = rectDeg(G, -2.5, 2.5, 8.5, 11);
  roundRectPath(ctx, m.x, m.y, m.w, m.h, 8);
  ctx.fillStyle = frame;
  ctx.fill();
  const mg = ctx.createLinearGradient(m.x, m.y, m.x + m.w, m.y + m.h);
  mg.addColorStop(0, p.night ? '#2a2f38' : '#8a95a3');
  mg.addColorStop(1, p.night ? '#151a22' : '#5f6a77');
  ctx.fillStyle = mg;
  ctx.fillRect(m.x + 6, m.y + 6, m.w - 12, m.h - 12);
}

function drawCluster(ctx: CanvasRenderingContext2D, p: Palette): void {
  const hood = rectDeg(G, -8.2, 0.2, -12.2, -7.6);
  roundRectPath(ctx, hood.x, hood.y, hood.w, hood.h, 14);
  ctx.fillStyle = p.night ? '#0b0c0e' : '#141517';
  ctx.fill();
  // Dial.
  const cx = -5.3;
  const cy = -10.6;
  const r = 2.0;
  ctx.fillStyle = '#0a0b0d';
  ellipseDeg(ctx, G, cx, cy, r, r);
  ctx.fill();
  ctx.strokeStyle = '#d8dde3';
  ctx.lineWidth = 2;
  const textColour = p.night ? '#d0d6dc' : '#e8edf1';
  for (let k = 0; k <= 9; k++) {
    const a = Math.PI * (1.2 - (k / 9) * 1.4);
    const inner = r - 0.35;
    ctx.beginPath();
    ctx.moveTo(G.X(cx + Math.cos(a) * inner), G.Y(cy + Math.sin(a) * inner));
    ctx.lineTo(G.X(cx + Math.cos(a) * (r - 0.12)), G.Y(cy + Math.sin(a) * (r - 0.12)));
    ctx.stroke();
    setFont(ctx, 0.38 * G.ppd, 600);
    ctx.fillStyle = textColour;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(k * 20), G.X(cx + Math.cos(a) * (r - 0.7)), G.Y(cy + Math.sin(a) * (r - 0.7)));
  }
  ctx.textBaseline = 'alphabetic';
  // Needle at 57 km/h.
  const an = Math.PI * (1.2 - (57 / 180) * 1.4);
  ctx.strokeStyle = '#e03a2f';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(G.X(cx), G.Y(cy));
  ctx.lineTo(G.X(cx + Math.cos(an) * (r - 0.3)), G.Y(cy + Math.sin(an) * (r - 0.3)));
  ctx.stroke();
  // Digital readout.
  textDeg(ctx, G, '57', -0.5, -10.2, 1.1, textColour, 700, 'right');
  textDeg(ctx, G, 'km/h', -0.5, -11.2, 0.4, textColour, 500, 'right');
}

function drawNav(ctx: CanvasRenderingContext2D, p: Palette): void {
  const bezel = rectDeg(G, 8, 20, -12.2, -4);
  roundRectPath(ctx, bezel.x, bezel.y, bezel.w, bezel.h, 12);
  ctx.fillStyle = p.night ? '#0b0c0e' : '#141517';
  ctx.fill();
  const s = rectDeg(G, 8.35, 19.65, -11.9, -4.3);
  ctx.fillStyle = '#1e2430';
  ctx.fillRect(s.x, s.y, s.w, s.h);
  ctx.save();
  ctx.beginPath();
  ctx.rect(s.x, s.y, s.w, s.h);
  ctx.clip();
  // Streets.
  ctx.strokeStyle = '#3a4456';
  ctx.lineWidth = 6;
  for (let i = 0; i < 5; i++) {
    ctx.beginPath();
    ctx.moveTo(s.x, s.y + 30 + i * 46);
    ctx.lineTo(s.x + s.w, s.y + 10 + i * 50);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(s.x + 20 + i * 70, s.y);
    ctx.lineTo(s.x + 40 + i * 70, s.y + s.h);
    ctx.stroke();
  }
  // Route.
  ctx.strokeStyle = '#2ec4b6';
  ctx.lineWidth = 8;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(s.x + s.w * 0.5, s.y + s.h);
  ctx.lineTo(s.x + s.w * 0.52, s.y + s.h * 0.45);
  ctx.lineTo(s.x + s.w * 0.85, s.y + s.h * 0.38);
  ctx.stroke();
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(s.x + s.w * 0.5, s.y + s.h * 0.9, 7, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // Top bar.
  fillRectDeg(ctx, G, 8.35, 19.65, -5.2, -4.3, '#101520');
  textDeg(ctx, G, '450 m ↱ Mustaqillik koʻchasi', 8.7, -4.95, 0.33, '#f2f5f8', 600);
  textDeg(ctx, G, '18:42', 19.3, -4.95, 0.33, '#9fb3c8', 600, 'right');
}

/**
 * A phone with navigation in a dashboard holder at 45 cm (expert feedback round 3, D-027): the near object of
 * the driving scenes, so that near-vision changes (presbyopia, myopia after laser correction) show up here too.
 */
function drawPhone(ctx: CanvasRenderingContext2D, p: Palette, lang: Lang): void {
  const x0 = 0.45;
  const x1 = 7.35;
  const top = -4.85;
  // Holder clamps behind the phone.
  fillRectDeg(ctx, G, x0 - 0.35, x0 + 0.1, -9.5, -6.8, '#1b1c1f');
  fillRectDeg(ctx, G, x1 - 0.1, x1 + 0.35, -9.5, -6.8, '#1b1c1f');
  const body = rectDeg(G, x0 - 0.25, x1 + 0.25, -13, top + 0.25);
  roundRectPath(ctx, body.x, body.y, body.w, body.h, 0.6 * G.ppd);
  ctx.fillStyle = '#0d0e10';
  ctx.fill();
  const screen = rectDeg(G, x0, x1, -13, top);
  ctx.save();
  roundRectPath(ctx, screen.x, screen.y, screen.w, screen.h, 0.4 * G.ppd);
  ctx.clip();
  const bg = p.night ? '#15191e' : '#f4f6f8';
  const ink = p.night ? '#e6ebef' : '#1a2026';
  const muted = p.night ? '#9aa6b0' : '#5b6874';
  ctx.fillStyle = bg;
  ctx.fillRect(screen.x, screen.y, screen.w, screen.h);
  textDeg(ctx, G, '18:42', x0 + 0.35, top - 0.38, 0.2, muted, 600);
  textDeg(ctx, G, '▮ 78%', x1 - 0.35, top - 0.38, 0.2, muted, 600, 'right');
  // Next manoeuvre banner.
  fillRectDeg(ctx, G, x0, x1, -7.45, -5.45, p.night ? '#0b5e66' : '#0e7c86');
  textDeg(ctx, G, '↱', x0 + 0.3, -6.75, 0.85, '#ffffff', 700);
  textDeg(ctx, G, '300 m', x0 + 1.75, -6.2, 0.5, '#ffffff', 700);
  textDeg(ctx, G, t(lang, 'city.phone.turn'), x0 + 1.75, -6.98, 0.28, '#e2f4f6', 500);
  textDeg(ctx, G, 'Mustaqillik koʻchasi', x0 + 0.35, -7.95, 0.28, ink, 600);
  textDeg(ctx, G, t(lang, 'city.phone.eta'), x0 + 0.35, -8.6, 0.24, muted, 500);
  // Map with the route.
  fillRectDeg(ctx, G, x0, x1, -13, -9.0, p.night ? '#1f262e' : '#dfe6dd');
  ctx.strokeStyle = p.night ? '#36414d' : '#ffffff';
  ctx.lineWidth = 0.18 * G.ppd;
  ctx.beginPath();
  ctx.moveTo(G.X(x0), G.Y(-10.4));
  ctx.lineTo(G.X(x1), G.Y(-9.8));
  ctx.moveTo(G.X(x0 + 2.4), G.Y(-13));
  ctx.lineTo(G.X(x0 + 2.9), G.Y(-9.0));
  ctx.stroke();
  ctx.strokeStyle = '#2ec4b6';
  ctx.lineWidth = 0.14 * G.ppd;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(G.X(x0 + 2.55), G.Y(-12.4));
  ctx.lineTo(G.X(x0 + 2.75), G.Y(-10.15));
  ctx.lineTo(G.X(x1 - 0.4), G.Y(-9.9));
  ctx.stroke();
  ctx.restore();
}

/**
 * Night emitters (D-015). SCENES §4 sizes are full ellipse widths (0.35° × 0.22° for a headlight at ~40 m),
 * so the radii are half of that; intensities are scaled up so that a lamp outshines the night road
 * (~0.03 linear) by 10⁴, as real headlights do.
 */
function nightLights(): SceneLight[] {
  const white: [number, number, number] = [0.9, 0.95, 1.0];
  const warm: [number, number, number] = [1.0, 0.85, 0.6];
  const red: [number, number, number] = [1, 0.12, 0.08];
  const lights: SceneLight[] = [
    // Oncoming headlights: two cars, pairs 2° and 1.3° apart.
    { layer: 'far', x: -3.6, y: 0.02, rx: 0.175, ry: 0.11, color: white, intensity: 400 },
    { layer: 'far', x: -1.6, y: 0.02, rx: 0.175, ry: 0.11, color: white, intensity: 400 },
    { layer: 'far', x: -2.85, y: 0.72, rx: 0.11, ry: 0.07, color: white, intensity: 400 },
    { layer: 'far', x: -1.55, y: 0.72, rx: 0.11, ry: 0.07, color: white, intensity: 400 },
    // Taillights of the car ahead.
    { layer: 'far', x: -0.55, y: 0.6, rx: 0.14, ry: 0.08, color: red, intensity: 60 },
    { layer: 'far', x: 1.95, y: 0.6, rx: 0.14, ry: 0.08, color: red, intensity: 60 },
    // Traffic light, red lamp (diameter 0.5°).
    { layer: 'far', x: -6, y: 9.0, rx: 0.25, ry: 0.25, color: [1, 0.18, 0.12], intensity: 120 },
  ];
  for (const [x, base, top] of LAMPS) {
    const size = (top - base) * 0.04;
    lights.push({ layer: 'far', x: x - (top - base) * 0.12, y: top - 0.05, rx: size, ry: size * 0.65, color: warm, intensity: 200 });
  }
  return lights;
}

function makeCity(id: 'city_day' | 'city_night', p: Palette): Scene {
  return {
    id,
    hfov: HFOV,
    lighting: p.night ? 'night' : 'day',
    exposure: 1.0,
    layers: [
      p.night
        // Shop neon lettering glows at 5× diffuse white (SCENES §4).
        ? { id: 'far', V: 0.05, draw: (ctx) => drawFar(ctx, p), emissive: (ctx) => drawNeon(ctx, p), emissiveScale: 5 }
        : { id: 'far', V: 0.05, draw: (ctx) => drawFar(ctx, p) },
      { id: 'mid', V: 1.33, draw: (ctx) => drawMid(ctx, p) },
      { id: 'near', V: 2.2, draw: (ctx, lang) => drawPhone(ctx, p, lang) },
    ],
    lights: () => (p.night ? nightLights() : []),
    tags: [
      { x: 0.7, y: 2.5, key: 'tag.m30' },
      { x: 14, y: -3.5, key: 'tag.cm75' },
      { x: 3.9, y: -4.1, key: 'tag.cm45' },
    ],
    ...(p.night ? { tone: NIGHT_TONE } : {}),
  };
}

export const cityDayScene = makeCity('city_day', DAY);
export const cityNightScene = makeCity('city_night', NIGHT);
